import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Chapter, { FadeIn } from "./Chapter";
import { SPOTS, LATE_NIGHT, mapsUrl } from "../data/spots";

const pin = (rank, top) =>
  L.divIcon({
    className: "",
    html: `<div class="map-pin${top ? " top" : ""}">${rank}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -18],
  });

const bounds = L.latLngBounds(SPOTS.map((s) => [s.lat, s.lng]));

export const MapSection = () => (
  <>
    <Chapter no="04" kicker="Location Intelligence" title="The Bar Proximity Strategy" id="the-map" />
    <section className="px-4 sm:px-8 pb-28" data-testid="map-section">
      <div className="grid lg:grid-cols-12 gap-10">
        <FadeIn className="lg:col-span-7 no-print">
          <div className="border-2 border-ink shadow-[8px_8px_0_rgba(26,26,26,0.9)] h-[60vh] lg:h-[72vh]">
            <MapContainer
              bounds={bounds}
              boundsOptions={{ padding: [50, 50] }}
              scrollWheelZoom={false}
              className="h-full w-full"
              data-testid="spot-map"
            >
              <TileLayer
                attribution='Tiles &copy; Esri — Esri, HERE, Garmin, &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                maxZoom={16}
              />
              {SPOTS.map((s) => (
                <Marker key={s.id} position={[s.lat, s.lng]} icon={pin(s.rank, s.score >= 9)}>
                  <Popup>
                    <div data-testid={`map-popup-${s.id}`} className="min-w-[180px]">
                      <p className="font-bold text-xs uppercase tracking-wider">
                        {String(s.rank).padStart(2, "0")} — {s.name}
                      </p>
                      <p className="text-[11px] mt-1">
                        Composite <span className="font-bold text-[#D9381E]">{s.score.toFixed(2)}</span> / 10
                      </p>
                      <p className="text-[10px] mt-1 text-neutral-600">{s.address}</p>
                      <a
                        href={mapsUrl(s)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10px] font-bold uppercase tracking-wider text-[#D9381E] underline mt-2 inline-block"
                      >
                        Open in Google Maps
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
          <p className="mt-3 font-mono text-[10px] tracking-[0.18em] uppercase text-muted2">
            Fig. 03 — All 17 spots. Red pins scored 9.0 or above. Click a pin for details.
          </p>
        </FadeIn>

        <div className="lg:col-span-5 space-y-8">
          <FadeIn className="font-body text-base md:text-lg leading-relaxed text-ink2 space-y-5">
            <p>
              Location relative to bars and nightlife is the most underanalyzed advantage in Austin's
              taco economy. A truck parked outside a bar at midnight accesses a hungry, spending,
              less price-sensitive customer base — and the social context of tacos after drinks
              creates the memory associations that drive return visits.
            </p>
            <p>
              The result is a <span className="text-ink font-medium">two-shift operation</span>:
              daytime neighborhood traffic plus late-night bar traffic, from a single location.
              For a small business owner, proximity to a bar on a Friday night is worth more than
              any Instagram campaign.
            </p>
          </FadeIn>
          <FadeIn delay={0.05} className="border border-dashed border-ink/50 p-6" data-testid="bar-field-note">
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-salsa mb-3">
              Field Note — Observed, South Congress
            </p>
            <p className="font-serif italic text-xl md:text-2xl leading-snug">
              "I watched a very intoxicated man leave the bar that shares a parking lot with La
              Casita del Pastor and stumble his way to the truck. He could barely speak. He still
              demolished six tacos — an easy $30 bill, right there."
            </p>
            <p className="mt-3 font-mono text-[10px] tracking-[0.14em] uppercase text-muted2">
              The bar proximity strategy, in one customer.
            </p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-muted2 mb-3">
              4 of the top 10 use the strategy
            </p>
            <div className="border-t-2 border-ink" data-testid="late-night-list">
              {LATE_NIGHT.map((l, i) => (
                <div key={l.name} className="flex gap-4 border-b border-ink/20 py-4">
                  <span className="font-mono text-xs text-salsa font-bold">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <p className="font-serif font-semibold text-xl leading-tight">{l.name}</p>
                    <p className="font-mono text-[10px] tracking-[0.1em] uppercase text-ink2 mt-1">{l.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  </>
);

export default MapSection;
